import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';
import { useAlerts, useBulkResolveAlerts, useResolveAlert } from '../hooks/useDataQuality';
import type { Alert } from '../data-quality.api';
import { CalculatedAtCaption } from './CalculatedAtCaption';

/**
 * FR24: DQS-alerts met per-alert en bulk-afhandeling. Optimistic UI voor het per-alert
 * resolve; falen daar wordt inline getoond (nooit als toast — een fout die actie vereist
 * hoort niet in een niet-blokkerende melding). Bulk-resolve toont een expliciet resultaat per
 * ID, nooit een stil half-gelukt resultaat.
 *
 * fu-3/AC2: DQS reports rule-anomaly alerts (a completeness rule overridden too often, or
 * auto-disabled as a result) — there is no backend-supplied free-text message. The display
 * message is built here, per anomaly type, from the structured fields via i18n (never a
 * hardcoded/backend string, per the i18n governance rule).
 */
function useAlertMessage() {
  const { t } = useTranslation();
  return function alertMessage(alert: Alert): string {
    const key = alert.anomalyType === 'auto_disabled'
      ? 'dataQuality.alerts.message.autoDisabled'
      : 'dataQuality.alerts.message.highOverrideRate';
    return t(key, {
      ruleName: alert.ruleName,
      overrideCount: alert.overrideCount,
      totalFindingsCount: alert.totalFindingsCount,
      overridePercentage: alert.overridePercentage,
    });
  };
}

export function AlertsPanel({ gln }: { gln: string }) {
  const { t } = useTranslation();
  const alertMessage = useAlertMessage();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data, isLoading } = useAlerts({ gln, status: 'open' });
  const resolveAlert = useResolveAlert();
  const bulkResolveAlerts = useBulkResolveAlerts();
  const alerts = data?.data ?? [];

  function toggleSelect(alertId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(alertId)) next.delete(alertId);
      else next.add(alertId);
      return next;
    });
  }

  function handleResolve(alertId: string) {
    resolveAlert.mutate(alertId);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(alertId);
      return next;
    });
  }

  function handleBulkResolve() {
    bulkResolveAlerts.mutate([...selected], { onSuccess: () => setSelected(new Set()) });
  }

  if (isLoading) {
    return <div data-testid="alerts-panel-skeleton" className="skeleton h-24 rounded-xl" />;
  }

  if (alerts.length === 0) {
    return (
      <div data-testid="alerts-panel-empty" className="rounded-xl p-4 text-xs" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', color: 'var(--foreground-muted)' }}>
        {t('dataQuality.alerts.empty')}
      </div>
    );
  }

  return (
    <div data-testid="alerts-panel" className="rounded-xl" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <p className="text-xs font-semibold" style={{ color: 'var(--foreground-heading)' }}>
          {t('dataQuality.alerts.title', { count: alerts.length })}
        </p>
      </div>

      <ul>
        {alerts.map((alert) => {
          const isResolving = resolveAlert.isPending && resolveAlert.variables === alert.alertId;
          const resolveFailed = resolveAlert.isError && resolveAlert.variables === alert.alertId;
          const message = alertMessage(alert);

          return (
            <li key={alert.alertId} className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid var(--border-soft)' }}>
              <button
                aria-label={t(selected.has(alert.alertId) ? 'dataQuality.alerts.deselect' : 'dataQuality.alerts.select', { message })}
                aria-pressed={selected.has(alert.alertId)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded border"
                data-testid={`alert-select-${alert.alertId}`}
                onClick={() => toggleSelect(alert.alertId)}
                style={{ borderColor: 'var(--border)' }}
                type="button"
              >
                {selected.has(alert.alertId) && <span aria-hidden="true" className="material-symbols-outlined text-[14px]">check</span>}
              </button>

              <div className="flex-1">
                <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{message}</p>
                <CalculatedAtCaption isoDate={alert.detectedAt} />
                {resolveFailed && (
                  <p data-testid={`alert-resolve-error-${alert.alertId}`} className="mt-1 text-xs" style={{ color: 'var(--destructive)' }}>
                    {t('dataQuality.alerts.resolveError')}
                  </p>
                )}
              </div>

              <Button
                aria-label={t('dataQuality.alerts.resolveActionFor', { message })}
                data-testid={`alert-resolve-${alert.alertId}`}
                disabled={isResolving}
                onClick={() => handleResolve(alert.alertId)}
                size="sm"
                variant="outline"
              >
                {t('dataQuality.alerts.resolveAction')}
              </Button>
            </li>
          );
        })}
      </ul>

      {selected.size > 0 && (
        <div
          data-testid="alerts-bulk-bar"
          className="flex items-center gap-3 px-4 py-3"
          role="toolbar"
          aria-label={t('dataQuality.alerts.bulkBarLabel')}
        >
          <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>
            {t('dataQuality.alerts.selectedCount', { count: selected.size })}
          </span>
          <Button data-testid="alerts-bulk-resolve" disabled={bulkResolveAlerts.isPending} onClick={handleBulkResolve} size="sm">
            {t('dataQuality.alerts.bulkResolveAction')}
          </Button>
        </div>
      )}

      {bulkResolveAlerts.data && bulkResolveAlerts.data.data.resolvedCount < bulkResolveAlerts.data.data.requestedCount && (
        <div data-testid="alerts-bulk-partial-failure" className="px-4 py-3 text-xs" style={{ color: 'var(--destructive)' }}>
          {t('dataQuality.alerts.bulkPartialFailure', { count: bulkResolveAlerts.data.data.requestedCount - bulkResolveAlerts.data.data.resolvedCount })}
        </div>
      )}
    </div>
  );
}
